import { Calculation } from 'final-form-calculate'
import { TipoAtendimentoEnum, TipoAtendimentoProfissional, TipoEstabelecimentoEnum } from 'graphql/types.generated'
import { ProcedimentosAutomaticosAtendimentoIndividual } from 'types/enums'
import { emptyArray } from 'util/array'
import { MetaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { AntropometriaAccordionModel } from 'view/atendimentos/components/MedicoesPanel/AntropometriaAccordion'
import { SinaisVitaisAccordionModel } from 'view/atendimentos/components/MedicoesPanel/SinaisVitaisAccordion'
import { ProcedimentoAutomatico } from 'view/atendimentos/model-atendimento'
import {
  addProcedimentoAutomatico,
  calculateProcedimentoAutomaticoToAdd,
  removeProcedimentoAutomatico,
  shouldAddProcedimentoAutomatico,
} from 'view/atendimentos/utils/util-procedimentoAutomatico'

import { FinalizacaoAtendimentoFormModel } from '../finalizacao'
import {
  findProcedimentosAutomaticosByCodigo,
  getProcedimentosWithNewProced,
} from '../finalizacao/calculator/calculator-individual'
import { ManterCidadaoLista } from '../finalizacao/components/DesfechoAtendimentoPanel'
import { ObjetivoFormModel } from '../objetivo'
import { FormAtivoObjetivoEnum } from '../objetivo/components/SwitchButtonObjetivoForm'
import { AvaliacaoDesenvolvimentoModel, PuericulturaModel } from '../objetivo/puericultura/model'
import { createPlanoMedicoesCalculations, HasAccessProcedimentos } from './calculator-plano-medicoes'
import { createPlanoOdontoCalculations } from './calculator-plano-odonto'
import { PlanoFormModel } from './PlanoForm'

export interface CreatePlanoCalculationsProps {
  plano: MetaPath<PlanoFormModel>
  objetivo: MetaPath<ObjetivoFormModel>
  formAtivoPath: MetaPath<FormAtivoObjetivoEnum>
  sinaisVitais: MetaPath<SinaisVitaisAccordionModel>
  antropometria: MetaPath<AntropometriaAccordionModel>
  puericultura: MetaPath<PuericulturaModel>
  finalizacao: MetaPath<FinalizacaoAtendimentoFormModel>
  procedimentosAutomaticos: ProcedimentoAutomatico[]
  idadeCidadaoEmAnos: number
  hasAccessProcedimentos: HasAccessProcedimentos
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
  tipoEstabelecimento: TipoEstabelecimentoEnum
  cbo2002: string
  isRegistroTardio: boolean
}

export const createPlanoCalculations = (props: CreatePlanoCalculationsProps): Calculation[] => {
  const {
    plano,
    objetivo,
    formAtivoPath,
    antropometria,
    puericultura,
    finalizacao,
    procedimentosAutomaticos,
    idadeCidadaoEmAnos,
    hasAccessProcedimentos,
    tipoAtendimentoProfissional,
    tipoEstabelecimento,
    cbo2002,
    isRegistroTardio,
  } = props
  return [
    ...createPlanoOdontoCalculations(plano.odontologia.periodontia),
    ...createPlanoMedicoesCalculations(plano, objetivo.sinaisVitais, procedimentosAutomaticos, hasAccessProcedimentos),
    createPlanoAtendimentoIndividualPuericulturaCalculation(
      plano,
      formAtivoPath,
      antropometria,
      procedimentosAutomaticos,
      idadeCidadaoEmAnos,
      hasAccessProcedimentos
    ),
    {
      field: [puericultura.absolutePath()],
      updates: {
        [plano.intervencoesProcedimentos.procedimentosPlano.procedimentos.absolutePath()]: (
          values: PuericulturaModel,
          allValues: SoapState
        ) => {
          const codigoProcedimentoSeraAdicionado =
            hasAccessProcedimentos.desenvolvimentoCrianca && procedToAddDesenvolvimentoPuericultura(values)

          const listaProcedimentos =
            allValues.plano?.intervencoesProcedimentos?.procedimentosPlano?.procedimentos ?? emptyArray
          const codigosProcedimentosAutomaticosExcluidos =
            allValues.plano?.intervencoesProcedimentos?.procedimentosPlano?.codigosProcedimentosAutomaticosExcluidos ??
            emptyArray

          if (
            codigoProcedimentoSeraAdicionado &&
            shouldAddProcedimentoAutomatico(codigoProcedimentoSeraAdicionado, codigosProcedimentosAutomaticosExcluidos)
          ) {
            return addProcedimentoAutomatico(
              listaProcedimentos,
              procedimentosAutomaticos,
              codigoProcedimentoSeraAdicionado
            )
          } else {
            return removeProcedimentoAutomatico(
              listaProcedimentos,
              ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_DESENVOLVIMENTO_PUERICULTURA
            )
          }
        },
      },
    },
    {
      field: plano.startObservacao.absolutePath(),
      updates: {
        [finalizacao.procedimentosAdministrativos.absolutePath()]: (_, allValues: SoapState) => {
          const isUpa = tipoEstabelecimento === TipoEstabelecimentoEnum.UPA

          if (isUpa) {
            const procedimentoAutomatico = calculateProcedimentoAutomaticoToAdd({
              tipoAtendimentoProfissional,
              tipoEstabelecimento,
              tipoAtendimento: allValues.finalizacao.tipoAtendimento,
              cbo2002,
              isProfResponsavelObservacao: allValues.plano?.startObservacao,
            })

            return getProcedimentosWithNewProced(
              allValues.finalizacao.procedimentosAdministrativos,
              findProcedimentosAutomaticosByCodigo(procedimentosAutomaticos, [procedimentoAutomatico])
            )
          }
          return allValues.finalizacao.procedimentosAdministrativos
        },
        [finalizacao.tipoAtendimento.absolutePath()]: (startObservacao: boolean, _, prevValues: SoapState) => {
          const isUpa = tipoEstabelecimento === TipoEstabelecimentoEnum.UPA
          if (startObservacao && isUpa) {
            return TipoAtendimentoEnum.URGENCIA
          } else {
            return prevValues?.finalizacao?.tipoAtendimento
          }
        },
        [finalizacao.desfechoAtendimento.manterCidadaoLista.absolutePath()]: (
          startObservacao: boolean,
          _,
          prevValue: SoapState
        ) => {
          if (startObservacao && !isRegistroTardio) {
            return ManterCidadaoLista.SIM
          }
          return prevValue?.finalizacao?.desfechoAtendimento?.manterCidadaoLista
        },
      },
    },
  ]
}

export const createPlanoAtendimentoIndividualPuericulturaCalculation = (
  planoPath: MetaPath<PlanoFormModel>,
  formAtivoPath: MetaPath<FormAtivoObjetivoEnum>,
  antropometriaPath: MetaPath<AntropometriaAccordionModel>,
  procedimentosAutomaticos: ReadonlyArray<ProcedimentoAutomatico>,
  idadeCidadaoEmAnos: number,
  hasAccessProcedimentos: HasAccessProcedimentos
): Calculation => ({
  field: [
    formAtivoPath.absolutePath(),
    antropometriaPath.peso.absolutePath(),
    antropometriaPath.altura.absolutePath(),
    antropometriaPath.perimetroCefalico.absolutePath(),
  ],
  updates: {
    [planoPath.intervencoesProcedimentos.procedimentosPlano.procedimentos.absolutePath()]: (
      _,
      allValues: SoapState
    ) => {
      const formAtivo = allValues.objetivo?.atendimentosEspecificos?.formAtivo
      let listaProcedimentos =
        allValues.plano?.intervencoesProcedimentos?.procedimentosPlano?.procedimentos ?? emptyArray

      const codigoProcedimentoSeraAdicionado =
        formAtivo === FormAtivoObjetivoEnum.PUERICULTURA && hasAccessProcedimentos.crescimentoCrianca
          ? procedToAddAtendimentoPuericultura(allValues, idadeCidadaoEmAnos)
          : procedToAddAtendimentoIndividual(allValues)

      const codigosProcedimentosAutomaticosExcluidos =
        allValues.plano?.intervencoesProcedimentos?.procedimentosPlano?.codigosProcedimentosAutomaticosExcluidos ??
        emptyArray

      if (
        codigoProcedimentoSeraAdicionado &&
        shouldAddProcedimentoAutomatico(codigoProcedimentoSeraAdicionado, codigosProcedimentosAutomaticosExcluidos)
      ) {
        listaProcedimentos = addProcedimentoAutomatico(
          listaProcedimentos,
          procedimentosAutomaticos,
          codigoProcedimentoSeraAdicionado
        )
      }

      PROCEDIMENTOS_TO_REMOVE.filter((proced) => proced !== codigoProcedimentoSeraAdicionado).forEach(
        (procedimento: ProcedimentosAutomaticosAtendimentoIndividual) => {
          listaProcedimentos = removeProcedimentoAutomatico(listaProcedimentos, procedimento)
        }
      )

      return listaProcedimentos
    },
  },
})

const procedToAddAtendimentoIndividual = (allValues: SoapState) => {
  const pesoPreenchido = allValues.objetivo?.antropometria?.peso
  const alturaPreenchida = allValues.objetivo?.antropometria?.altura

  if (pesoPreenchido && alturaPreenchida) {
    return ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_ANTROPOMETRICA
  } else if (pesoPreenchido) {
    return ProcedimentosAutomaticosAtendimentoIndividual.MEDICAO_DE_PESO
  } else if (alturaPreenchida) {
    return ProcedimentosAutomaticosAtendimentoIndividual.MEDICAO_DE_ALTURA
  } else {
    return null
  }
}

const procedToAddAtendimentoPuericultura = (allValues: SoapState, idadeCidadao: number) => {
  const pesoPreenchido = allValues.objetivo?.antropometria?.peso
  const alturaPreenchida = allValues.objetivo?.antropometria?.altura
  const perimetroCefPreenchido = allValues.objetivo?.antropometria?.perimetroCefalico

  return pesoPreenchido && !alturaPreenchida
    ? ProcedimentosAutomaticosAtendimentoIndividual.MEDICAO_DE_PESO
    : !pesoPreenchido && alturaPreenchida
    ? ProcedimentosAutomaticosAtendimentoIndividual.MEDICAO_DE_ALTURA
    : pesoPreenchido && alturaPreenchida
    ? idadeCidadao >= 2 || (idadeCidadao < 2 && perimetroCefPreenchido)
      ? ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_CRESCIMENTO_PUERICULTURA
      : ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_ANTROPOMETRICA
    : null
}

const procedToAddDesenvolvimentoPuericultura = (puericultura: PuericulturaModel) => {
  const alteracoesFenotipicas = puericultura?.alteracoesFenotipicas?.alteracoesFenotipicas
  const fatoresRisco = puericultura?.fatoresRisco?.fatoresRisco

  const hasChangedMarcos = (): boolean => {
    const marcosDesenvolvimento = puericultura?.marcosDesenvolvimento

    return (
      marcosDesenvolvimento &&
      Object.values(marcosDesenvolvimento.marcosRecord)?.some((marcoGrupo) => hasChanged(marcoGrupo))
    )
  }

  const hasChanged = (avaliacao: AvaliacaoDesenvolvimentoModel[]) =>
    avaliacao?.some((item) => item.statusButton !== item.status)

  return hasChanged(alteracoesFenotipicas) || hasChanged(fatoresRisco) || hasChangedMarcos()
    ? ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_DESENVOLVIMENTO_PUERICULTURA
    : null
}

const PROCEDIMENTOS_TO_REMOVE = [
  ProcedimentosAutomaticosAtendimentoIndividual.MEDICAO_DE_PESO,
  ProcedimentosAutomaticosAtendimentoIndividual.MEDICAO_DE_ALTURA,
  ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_ANTROPOMETRICA,
  ProcedimentosAutomaticosAtendimentoIndividual.AVALIACAO_CRESCIMENTO_PUERICULTURA,
]
